/**
 * TMA 轻量 i18n：浏览器语言 / Telegram / IP 建议 / 用户手动选择
 * 手动选择优先 → localStorage → 用户 profile → 自动检测
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getToken, patchMe, request } from "./api";
import { getWebApp } from "./telegram";

export type LocaleCode = "zh-CN" | "en" | "ja" | "ko";

export const SUPPORTED_LOCALES: { code: LocaleCode; label: string; native: string }[] = [
  { code: "zh-CN", label: "Chinese", native: "简体中文" },
  { code: "en", label: "English", native: "English" },
  { code: "ja", label: "Japanese", native: "日本語" },
  { code: "ko", label: "Korean", native: "한국어" },
];

const STORAGE_KEY = "tma_locale";
const STORAGE_SOURCE = "tma_locale_source"; // manual | auto

type Dict = Record<string, string>;

const zh: Dict = {
  tabExplore: "套餐",
  tabOrders: "订单",
  tabEsim: "eSIM",
  tabAccount: "我的",
  connecting: "正在连接…",
  secureLogin: "安全登录",
  loginHint: "在 Telegram Mini App 内将自动鉴权；浏览器调试请确认后端已开启 Dev Bypass。",
  continueLogin: "继续登录",
  heroTitle: "全球 eSIM",
  heroSub: "即买即用 · 支持 TON / USDT 链上支付",
  searchPlaceholder: "搜索国家或套餐…",
  noPackages: "暂无套餐，请先在管理端同步目录",
  dataPlan: "流量套餐",
  days: "天",
  payCrypto: "加密货币支付",
  myOrders: "我的订单",
  ordersSub: "查看购买与支付状态",
  noOrders: "暂无订单",
  myEsims: "我的 eSIM",
  esimsSub: "安装资料与用量状态",
  noEsims: "购买成功后将显示安装信息",
  account: "我的",
  accountSub: "账号与钱包",
  accountLabel: "账号",
  tonWallet: "TON 钱包",
  tonWalletHint: "连接后可在收银台一键支付 TON / USDT（Jetton）",
  securityTip: "安全提示",
  securityBody: "请仅向收银台展示的收款地址转账，并核对金额与 Memo。",
  logout: "退出登录",
  language: "语言",
  languageSub: "界面显示语言",
  languageAuto: "跟随系统 / 网络",
  languageSaved: "已保存",
  pickAsset: "选择支付资产",
  pickAssetSub: "支持 TON / USDT 等多网络",
  continue: "继续",
  payAmount: "支付金额",
  receiveAddr: "收款地址",
  memo: "备注 / Memo（务必填写）",
  expires: "过期时间",
  changeAsset: "更换资产",
  paySuccess: "支付成功",
  paySuccessHint: "订单正在开通 eSIM，请稍后在「eSIM」查看",
  viewEsims: "查看我的 eSIM",
  waitingConfirm: "等待链上确认中…",
  recommendedWallets: "推荐钱包",
  noAssets: "暂无启用资产，请配置收款地址",
  loading: "加载中…",
  backHome: "返回首页",
  refresh: "刷新页面",
  pageError: "页面出错",
  source_manual: "手动设置",
  source_profile: "账号偏好",
  source_telegram: "Telegram",
  source_browser: "浏览器",
  source_ip: "网络位置",
  source_server: "服务器建议",
  source_default: "默认",
  detectAgain: "重新自动检测",
  destinations: "热门目的地",
  plansSection: "套餐",
  packagesCount: "{n} 个套餐",
  priceFrom: "从 {price}",
  coverage: "覆盖范围",
  globalCoverage: "全球 / 多地区覆盖",
  continuePay: "继续支付",
  cancelOrder: "取消订单",
  cancelFailed: "取消失败",
  filterSort: "排序",
  filterScope: "覆盖范围",
  filterDays: "有效天数",
  filterData: "流量",
  filterPrice: "价格",
  sortRecommend: "推荐",
  sortPriceAsc: "价格↑",
  sortPriceDesc: "价格↓",
  sortData: "流量",
  sortDays: "天数",
  filterScopeAll: "全部",
  filterScopeLocal: "本国",
  filterScopeMulti: "区域/全球",
  filterDay1: "1 天",
  filterDays1_7: "1–7 天",
  filterDays8_15: "8–15 天",
  filterDays16_30: "16–30 天",
  filterDays30p: "30 天+",
  filtersActive: "已选 {n} 项筛选",
  filterReset: "清除",
  filterNoResults: "没有符合条件的套餐",
  menuSectionService: "服务",
  menuSectionWallet: "钱包",
  menuSectionPrefs: "偏好",
  menuSectionSupport: "支持与社区",
  menuSectionLegal: "法律信息",
  menuTransactions: "交易记录",
  menuTransactionsSub: "订单与支付状态",
  menuTelegramChannel: "Telegram 频道",
  menuTelegramChannelSub: "公告、活动与上新",
  menuContactSupport: "联系支持",
  menuContactSupportSub: "客服与安装协助",
  menuTerms: "使用条款",
  menuPrivacy: "隐私政策",
  legalUpdated: "更新于 {date}",
  legalFullOnWeb: "在官网查看完整版本",
  orderSummary: "订单简介",
  orderNo: "订单号",
  orderNotFound: "订单不存在或无法加载",
  payApproxCrypto: "约 {amount} {symbol}",
  continuePayWith: "支付 {amount}",
  geoGlobal: "全球多地",
  geoMultiCount: "{n} 国区域",
  menuSectionHelp: "帮助",
  helpCenter: "帮助中心",
  helpCenterSub: "购买 · 支付 · 安装 · FAQ（EGoeSIM）",
  helpOfficial: "官方指南",
  helpQuickStart: "四步上手",
  helpQuick1: "选国家与套餐",
  helpQuick2: "下单并用 TON / USDT 支付",
  helpQuick3: "在 eSIM 页安装配置",
  helpQuick4: "抵达后开启数据漫游",
  helpNotFound: "未找到该帮助文章",
  helpNotes: "注意事项",
  helpBackToIndex: "返回帮助目录",
  helpGoShop: "去选购套餐",
};

const en: Dict = {
  tabExplore: "Plans",
  tabOrders: "Orders",
  tabEsim: "eSIM",
  tabAccount: "Account",
  connecting: "Connecting…",
  secureLogin: "Secure sign-in",
  loginHint: "Auto-auth inside Telegram Mini App. For browser debug, enable Dev Bypass on API.",
  continueLogin: "Continue",
  heroTitle: "Travel eSIM",
  heroSub: "Instant delivery · Pay with TON / USDT",
  searchPlaceholder: "Search country or plan…",
  noPackages: "No plans yet. Sync catalog in admin first.",
  dataPlan: "Data plan",
  days: "days",
  payCrypto: "Pay with crypto",
  myOrders: "Orders",
  ordersSub: "Purchase and payment status",
  noOrders: "No orders yet",
  myEsims: "My eSIMs",
  esimsSub: "Install details and usage",
  noEsims: "Install info appears after purchase",
  account: "Account",
  accountSub: "Profile and wallet",
  accountLabel: "Profile",
  tonWallet: "TON wallet",
  tonWalletHint: "Connect to pay TON / USDT (Jetton) at checkout",
  securityTip: "Security",
  securityBody: "Only send to the address shown at checkout. Check amount and memo.",
  logout: "Sign out",
  language: "Language",
  languageSub: "App display language",
  languageAuto: "Match system / network",
  languageSaved: "Saved",
  pickAsset: "Select asset",
  pickAssetSub: "TON / USDT and more networks",
  continue: "Continue",
  payAmount: "Amount",
  receiveAddr: "Receive address",
  memo: "Memo (required)",
  expires: "Expires",
  changeAsset: "Change asset",
  paySuccess: "Payment successful",
  paySuccessHint: "We are provisioning your eSIM. Check the eSIM tab shortly.",
  viewEsims: "View my eSIMs",
  waitingConfirm: "Waiting for on-chain confirmation…",
  recommendedWallets: "Suggested wallets",
  noAssets: "No assets enabled. Configure receive addresses.",
  loading: "Loading…",
  backHome: "Home",
  refresh: "Reload",
  pageError: "Something went wrong",
  source_manual: "Manual",
  source_profile: "Profile",
  source_telegram: "Telegram",
  source_browser: "Browser",
  source_ip: "Network",
  source_server: "Server",
  source_default: "Default",
  detectAgain: "Auto-detect again",
  destinations: "Destinations",
  plansSection: "Plans",
  packagesCount: "{n} plans",
  priceFrom: "From {price}",
  coverage: "Coverage",
  globalCoverage: "Global / multi-region coverage",
  continuePay: "Continue payment",
  cancelOrder: "Cancel order",
  cancelFailed: "Cancel failed",
  filterSort: "Sort",
  filterScope: "Coverage",
  filterDays: "Duration",
  filterData: "Data",
  filterPrice: "Price",
  sortRecommend: "Recommended",
  sortPriceAsc: "Price ↑",
  sortPriceDesc: "Price ↓",
  sortData: "Data",
  sortDays: "Days",
  filterScopeAll: "All",
  filterScopeLocal: "Local",
  filterScopeMulti: "Regional",
  filterDay1: "1 day",
  filterDays1_7: "1–7 days",
  filterDays8_15: "8–15 days",
  filterDays16_30: "16–30 days",
  filterDays30p: "30+ days",
  filtersActive: "{n} filters active",
  filterReset: "Reset",
  filterNoResults: "No plans match filters",
  menuSectionService: "Services",
  menuSectionWallet: "Wallet",
  menuSectionPrefs: "Preferences",
  menuSectionSupport: "Support & community",
  menuSectionLegal: "Legal",
  menuTransactions: "Transaction history",
  menuTransactionsSub: "Orders and payment status",
  menuTelegramChannel: "Telegram channel",
  menuTelegramChannelSub: "News and product updates",
  menuContactSupport: "Contact support",
  menuContactSupportSub: "Help with install and billing",
  menuTerms: "Terms of use",
  menuPrivacy: "Privacy policy",
  legalUpdated: "Updated {date}",
  legalFullOnWeb: "View full version on website",
  orderSummary: "Order summary",
  orderNo: "Order",
  orderNotFound: "Order not found",
  payApproxCrypto: "≈ {amount} {symbol}",
  continuePayWith: "Pay {amount}",
  geoGlobal: "Global",
  geoMultiCount: "{n} countries",
  menuSectionHelp: "Help",
  helpCenter: "Help Center",
  helpCenterSub: "Buy · Pay · Install · FAQ (EGoeSIM)",
  helpOfficial: "Official guide",
  helpQuickStart: "Quick start",
  helpQuick1: "Pick a country and plan",
  helpQuick2: "Order and pay with TON / USDT",
  helpQuick3: "Install from the eSIM tab",
  helpQuick4: "Enable data roaming on arrival",
  helpNotFound: "Article not found",
  helpNotes: "Notes",
  helpBackToIndex: "Back to help index",
  helpGoShop: "Browse plans",
};

const ja: Dict = {
  ...en,
  tabExplore: "プラン",
  tabOrders: "注文",
  tabEsim: "eSIM",
  tabAccount: "アカウント",
  connecting: "接続中…",
  secureLogin: "安全なログイン",
  continueLogin: "続ける",
  heroTitle: "旅行用 eSIM",
  heroSub: "すぐ使える · TON / USDT 対応",
  searchPlaceholder: "国やプランを検索…",
  noPackages: "プランがありません",
  dataPlan: "データプラン",
  days: "日",
  payCrypto: "暗号資産で支払う",
  myOrders: "注文",
  ordersSub: "購入と支払い状況",
  noOrders: "注文はまだありません",
  myEsims: "マイ eSIM",
  esimsSub: "インストール情報",
  noEsims: "購入後に表示されます",
  account: "アカウント",
  accountSub: "プロフィールとウォレット",
  accountLabel: "プロフィール",
  tonWallet: "TON ウォレット",
  securityTip: "セキュリティ",
  logout: "ログアウト",
  language: "言語",
  languageSub: "表示言語",
  languageAuto: "システム / ネットワークに合わせる",
  languageSaved: "保存しました",
  pickAsset: "支払い資産を選択",
  continue: "続ける",
  payAmount: "金額",
  receiveAddr: "受取アドレス",
  changeAsset: "資産を変更",
  paySuccess: "支払い完了",
  viewEsims: "eSIM を見る",
  loading: "読み込み中…",
  detectAgain: "再検出",
  destinations: "人気の目的地",
  plansSection: "プラン",
  packagesCount: "{n} プラン",
  priceFrom: "{price} から",
  coverage: "対応エリア",
  globalCoverage: "グローバル / 複数地域",
  continuePay: "支払いを続ける",
  cancelOrder: "注文をキャンセル",
  cancelFailed: "キャンセル失敗",
  filterSort: "並び替え",
  filterScope: "カバー範囲",
  filterDays: "有効日数",
  filterData: "データ量",
  filterPrice: "価格",
  sortRecommend: "おすすめ",
  sortPriceAsc: "価格↑",
  sortPriceDesc: "価格↓",
  sortData: "データ",
  sortDays: "日数",
  filterScopeAll: "すべて",
  filterScopeLocal: "現地",
  filterScopeMulti: "広域",
  filterDay1: "1日",
  filterDays1_7: "1–7日",
  filterDays8_15: "8–15日",
  filterDays16_30: "16–30日",
  filterDays30p: "30日+",
  filtersActive: "{n}件の絞り込み",
  filterReset: "クリア",
  filterNoResults: "条件に合うプランがありません",
  menuSectionService: "サービス",
  menuSectionWallet: "ウォレット",
  menuSectionPrefs: "設定",
  menuSectionSupport: "サポート",
  menuSectionLegal: "法的情報",
  menuTransactions: "取引履歴",
  menuTransactionsSub: "注文と支払い",
  menuTelegramChannel: "Telegram チャンネル",
  menuTelegramChannelSub: "お知らせと更新",
  menuContactSupport: "サポートに連絡",
  menuContactSupportSub: "インストールと請求",
  menuTerms: "利用規約",
  menuPrivacy: "プライバシーポリシー",
  legalUpdated: "更新日 {date}",
  legalFullOnWeb: "公式サイトで全文を見る",
  orderSummary: "注文概要",
  orderNo: "注文番号",
  orderNotFound: "注文が見つかりません",
  payApproxCrypto: "約 {amount} {symbol}",
  continuePayWith: "{amount} を支払う",
  geoGlobal: "グローバル",
  geoMultiCount: "{n}か国",
  menuSectionHelp: "ヘルプ",
  helpCenter: "ヘルプセンター",
  helpCenterSub: "購入・支払い・インストール（EGoeSIM）",
  helpOfficial: "公式ガイド",
  helpQuickStart: "4ステップ",
  helpQuick1: "国とプランを選ぶ",
  helpQuick2: "TON / USDT で支払う",
  helpQuick3: "eSIM タブからインストール",
  helpQuick4: "到着後にローミングをオン",
  helpNotFound: "記事が見つかりません",
  helpNotes: "注意事項",
  helpBackToIndex: "目次に戻る",
  helpGoShop: "プランを見る",
};

const ko: Dict = {
  ...en,
  tabExplore: "요금제",
  tabOrders: "주문",
  tabEsim: "eSIM",
  tabAccount: "내 정보",
  connecting: "연결 중…",
  secureLogin: "안전한 로그인",
  continueLogin: "계속",
  heroTitle: "여행 eSIM",
  heroSub: "즉시 사용 · TON / USDT 결제",
  searchPlaceholder: "국가 또는 요금제 검색…",
  noPackages: "요금제가 없습니다",
  dataPlan: "데이터 요금제",
  days: "일",
  payCrypto: "암호화폐 결제",
  myOrders: "주문",
  noOrders: "주문이 없습니다",
  myEsims: "내 eSIM",
  noEsims: "구매 후 표시됩니다",
  account: "내 정보",
  accountLabel: "계정",
  tonWallet: "TON 지갑",
  securityTip: "보안",
  logout: "로그아웃",
  language: "언어",
  languageSub: "표시 언어",
  languageAuto: "시스템 / 네트워크 따름",
  languageSaved: "저장됨",
  pickAsset: "결제 자산 선택",
  continue: "계속",
  payAmount: "금액",
  receiveAddr: "입금 주소",
  changeAsset: "자산 변경",
  paySuccess: "결제 완료",
  viewEsims: "eSIM 보기",
  loading: "로딩 중…",
  detectAgain: "다시 감지",
  destinations: "인기 목적지",
  plansSection: "요금제",
  packagesCount: "요금제 {n}개",
  priceFrom: "{price}부터",
  coverage: "적용 범위",
  globalCoverage: "글로벌 / 다지역",
  continuePay: "결제 계속",
  cancelOrder: "주문 취소",
  cancelFailed: "취소 실패",
  filterSort: "정렬",
  filterScope: "적용 범위",
  filterDays: "유효 기간",
  filterData: "데이터",
  filterPrice: "가격",
  sortRecommend: "추천",
  sortPriceAsc: "가격↑",
  sortPriceDesc: "가격↓",
  sortData: "데이터",
  sortDays: "일수",
  filterScopeAll: "전체",
  filterScopeLocal: "현지",
  filterScopeMulti: "지역/글로벌",
  filterDay1: "1일",
  filterDays1_7: "1–7일",
  filterDays8_15: "8–15일",
  filterDays16_30: "16–30일",
  filterDays30p: "30일+",
  filtersActive: "필터 {n}개",
  filterReset: "초기화",
  filterNoResults: "조건에 맞는 요금제가 없습니다",
  menuSectionService: "서비스",
  menuSectionWallet: "지갑",
  menuSectionPrefs: "환경설정",
  menuSectionSupport: "지원 및 커뮤니티",
  menuSectionLegal: "법적 정보",
  menuTransactions: "거래 기록",
  menuTransactionsSub: "주문 및 결제 상태",
  menuTelegramChannel: "Telegram 채널",
  menuTelegramChannelSub: "공지와 업데이트",
  menuContactSupport: "고객 지원",
  menuContactSupportSub: "설치 및 결제 도움",
  menuTerms: "이용 약관",
  menuPrivacy: "개인정보 처리방침",
  legalUpdated: "{date} 업데이트",
  legalFullOnWeb: "웹사이트에서 전문 보기",
  orderSummary: "주문 요약",
  orderNo: "주문번호",
  orderNotFound: "주문을 찾을 수 없습니다",
  payApproxCrypto: "약 {amount} {symbol}",
  continuePayWith: "{amount} 결제",
  geoGlobal: "글로벌",
  geoMultiCount: "{n}개국",
  menuSectionHelp: "도움말",
  helpCenter: "도움말 센터",
  helpCenterSub: "구매 · 결제 · 설치 (EGoeSIM)",
  helpOfficial: "공식 가이드",
  helpQuickStart: "빠른 시작",
  helpQuick1: "국가와 요금제 선택",
  helpQuick2: "TON / USDT 로 결제",
  helpQuick3: "eSIM 탭에서 설치",
  helpQuick4: "도착 후 데이터 로밍",
  helpNotFound: "문서를 찾을 수 없습니다",
  helpNotes: "참고",
  helpBackToIndex: "목록으로",
  helpGoShop: "요금제 보기",
};

const DICTS: Record<LocaleCode, Dict> = { "zh-CN": zh, en, ja, ko };

export function normalizeLocale(raw?: string | null): LocaleCode | null {
  if (!raw) return null;
  const s = raw.trim().replace(/_/g, "-");
  const low = s.toLowerCase();
  if (low === "zh-cn" || low === "zh" || low.startsWith("zh-hans") || low === "zh-sg") return "zh-CN";
  if (low.startsWith("zh")) return "zh-CN";
  if (low === "ja" || low.startsWith("ja-")) return "ja";
  if (low === "ko" || low.startsWith("ko-")) return "ko";
  if (low === "en" || low.startsWith("en-")) return "en";
  return null;
}

/** 浏览器 Accept-Language / navigator */
export function detectBrowserLocale(): LocaleCode | null {
  const list = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const l of list) {
    const n = normalizeLocale(l);
    if (n) return n;
  }
  // UA 兜底
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("zh-cn") || ua.includes("zh_cn")) return "zh-CN";
  if (ua.includes("ja-") || ua.includes("lang/ja")) return "ja";
  if (ua.includes("ko-") || ua.includes("lang/ko")) return "ko";
  return null;
}

export function detectTelegramLocale(): LocaleCode | null {
  try {
    const code = getWebApp()?.initDataUnsafe?.user?.language_code;
    return normalizeLocale(code);
  } catch {
    return null;
  }
}

type SuggestResp = {
  locale?: string;
  source?: string;
  country?: string;
};

async function detectServerLocale(): Promise<{ locale: LocaleCode; source: string; country?: string } | null> {
  try {
    const data = await request<SuggestResp>("/api/v1/locale/suggest", { auth: false });
    const loc = normalizeLocale(data?.locale);
    if (!loc) return null;
    return { locale: loc, source: data.source || "server", country: data.country };
  } catch {
    return null;
  }
}

type I18nCtx = {
  locale: LocaleCode;
  source: string;
  t: (key: string, vars?: Record<string, string | number>) => string;
  setLocale: (loc: LocaleCode, manual?: boolean) => Promise<void>;
  resetAuto: () => Promise<void>;
  detecting: boolean;
};

const Ctx = createContext<I18nCtx | null>(null);

function applyDocumentLang(loc: LocaleCode) {
  try {
    document.documentElement.lang = loc;
  } catch {
    /* ignore */
  }
}

export function I18nProvider({
  children,
  profileLocale,
}: {
  children: ReactNode;
  /** 登录用户 profile.locale */
  profileLocale?: string | null;
}) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const n = normalizeLocale(saved);
      if (n) return n;
    } catch {
      /* ignore */
    }
    return detectBrowserLocale() || detectTelegramLocale() || "en";
  });
  const [source, setSource] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_SOURCE) || "browser";
    } catch {
      return "browser";
    }
  });
  const [detecting, setDetecting] = useState(false);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const d = DICTS[locale] || en;
      let s = d[key] || en[key] || key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        }
      }
      return s;
    },
    [locale],
  );

  const persist = useCallback(async (loc: LocaleCode, src: string, syncProfile: boolean) => {
    setLocaleState(loc);
    setSource(src);
    applyDocumentLang(loc);
    try {
      localStorage.setItem(STORAGE_KEY, loc);
      localStorage.setItem(STORAGE_SOURCE, src);
    } catch {
      /* ignore */
    }
    if (syncProfile && getToken()) {
      try {
        await patchMe(loc);
      } catch {
        /* 非阻断 */
      }
    }
  }, []);

  const setLocale = useCallback(
    async (loc: LocaleCode, manual = true) => {
      await persist(loc, manual ? "manual" : "auto", true);
    },
    [persist],
  );

  const runAutoDetect = useCallback(async () => {
    setDetecting(true);
    try {
      // 优先级：Telegram → 浏览器 → 服务端（IP/Accept-Language）
      const tg = detectTelegramLocale();
      if (tg) {
        await persist(tg, "telegram", true);
        return;
      }
      const br = detectBrowserLocale();
      if (br) {
        // 仍请求服务端，若 IP 国家更「强」且浏览器为英文默认，可用服务端覆盖
        const srv = await detectServerLocale();
        if (srv && br === "en" && srv.locale !== "en" && srv.source === "ip_country") {
          await persist(srv.locale, "ip", true);
          return;
        }
        await persist(br, "browser", true);
        // 后台对齐服务端建议（不强制覆盖浏览器明确语言）
        return;
      }
      const srv = await detectServerLocale();
      if (srv) {
        await persist(srv.locale, srv.source === "ip_country" ? "ip" : "server", true);
        return;
      }
      await persist("en", "default", true);
    } finally {
      setDetecting(false);
    }
  }, [persist]);

  const resetAuto = useCallback(async () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_SOURCE, "auto");
    } catch {
      /* ignore */
    }
    await runAutoDetect();
  }, [runAutoDetect]);

  // 首次：若非手动，则跑自动检测；profile 可覆盖 auto
  useEffect(() => {
    applyDocumentLang(locale);
    let cancelled = false;
    (async () => {
      let src = "browser";
      try {
        src = localStorage.getItem(STORAGE_SOURCE) || "browser";
      } catch {
        /* ignore */
      }
      if (src === "manual") return;
      // 登录用户已有 locale
      const pl = normalizeLocale(profileLocale);
      if (pl) {
        if (!cancelled) await persist(pl, "profile", false);
        return;
      }
      if (!cancelled) await runAutoDetect();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLocale]);

  const value = useMemo(
    () => ({ locale, source, t, setLocale, resetAuto, detecting }),
    [locale, source, t, setLocale, resetAuto, detecting],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n outside provider");
  return v;
}

export function sourceLabel(t: (k: string) => string, source: string): string {
  switch (source) {
    case "manual":
      return t("source_manual");
    case "profile":
      return t("source_profile");
    case "telegram":
      return t("source_telegram");
    case "browser":
      return t("source_browser");
    case "ip":
    case "ip_country":
      return t("source_ip");
    case "server":
    case "accept_language":
      return t("source_server");
    default:
      return t("source_default");
  }
}

